import { addToast, closeAll } from '@heroui/react';
import { AlertTriangle, CheckCircle, Languages, XCircle } from 'lucide-react';
import React, { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { isAILimitToastActive } from '../axios/axionInstance';
import { getLanguageName } from '../utils';
import { compactToastProps, TOAST_TIMEOUT } from '../utils/toastStyles';

export const useHeroToast = (): {
  showError: (message?: string, title?: string) => void;
  showSuccess: (message?: string, title?: string) => void;
  showWarning: (message?: string, title?: string) => void;
  showLangContextSuccess: (message?: string, title?: string, language?: string) => void;
  showPromise: <T = never>(
      action: () => Promise<T>,
      type?: 'delete' | 'regenerate' | 'update' | 'upload',
      isLoading?: ReactNode | null,
  ) => Promise<T>;
} => {
  const { t } = useTranslation();

  const showError = useCallback((message?: string, title?: string): void => {
    if (isAILimitToastActive()) return;

    addToast({
      ...compactToastProps('danger'),
      title: title || t('toast.error_title'),
      description: message || t('common.messages.request_error'),
      timeout: TOAST_TIMEOUT.default,
      icon: React.createElement(XCircle, { size: 16, strokeWidth: 2 }),
    });
  }, [t]);

  const showSuccess = useCallback((message?: string, title?: string): void => {
    addToast({
      ...compactToastProps('success'),
      title: title || t('toast.success_title'),
      description: message || t('common.messages.document_saved'),
      timeout: TOAST_TIMEOUT.short,
      icon: React.createElement(CheckCircle, { size: 16, strokeWidth: 2 }),
    });
  }, [t]);

  const showWarning = useCallback((message?: string, title?: string): void => {
    addToast({
      ...compactToastProps('warning'),
      title: title || t('toast.warning_title'),
      description: message || t('common.messages.something_went_wrong'),
      timeout: TOAST_TIMEOUT.default,
      icon: React.createElement(AlertTriangle, { size: 16, strokeWidth: 2 }),
    });
  }, [t]);

  const showLangContextSuccess = useCallback((
    message?: string,
    title?: string,
    language?: string,
  ): void => {
    addToast({
      ...compactToastProps('primary'),
      title: title || t('toast.language_changed'),
      description: message || t('toast.language_context_selected', { language: getLanguageName(language || '') }),
      timeout: TOAST_TIMEOUT.short,
      icon: React.createElement(Languages, { size: 16, strokeWidth: 2 }),
    });
  }, [t]);

  const showPromise = useCallback(async <T = never>(
    action: () => Promise<T>,
    type?: 'delete' | 'regenerate' | 'update' | 'upload',
    isLoading?: ReactNode | null,
  ): Promise<T> => {
    let title: string;
    let description: string;

    switch (type) {
      case 'delete':
        title = t('common.messages.deleting');
        description = t('common.messages.deleting_data');
        break;
      case 'regenerate':
        title = t('common.messages.fixing_translations');
        description = t('common.messages.fixing_translations_desc');
        break;
      case 'update':
        title = t('common.messages.updating_data');
        description = t('common.messages.updating_data_desc');
        break;
      case 'upload':
        title = t('common.messages.uploading');
        description = t('common.messages.uploading_file');
        break;
      default:
        title = t('common.messages.saving_in_progress');
        description = t('common.messages.creating_document');
    }

    addToast({
      ...compactToastProps('primary'),
      title,
      description,
      endContent: isLoading || '',
      timeout: TOAST_TIMEOUT.long,
    });

    try {
      const result = await action();
      closeAll();
      addToast({
        ...compactToastProps('success'),
        title: t('toast.success_title'),
        description: t('common.messages.data_updated'),
        timeout: TOAST_TIMEOUT.short,
        icon: React.createElement(CheckCircle, { size: 16, strokeWidth: 2 }),
      });
      return result;
    } catch (error) {
      closeAll();
      throw error;
    }
  }, [t]);

  return { showError, showSuccess, showWarning, showPromise, showLangContextSuccess };
};
